export type Translations = {
  [key: string]: string | Translations
}

export type TranslationsByLanguage = {
  [key: string]: Translations
}

export type TranslationParameters = Record<string, string>

export const isUndefined = (value: unknown): value is undefined =>
  value === undefined

export const isNull = (value: unknown): value is null => value === null

export const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && !isNull(value)

export const isString = (value: unknown): value is string =>
  typeof value === 'string'

export const getPropertyByPath = (obj: Translations, path: string): string => {
  const [first, ...rest] = path.split('.')
  const value = obj[first]
  if (isObject(value)) {
    return getPropertyByPath(value, rest.join('.'))
  }
  return value
}

export const interpolate = (
  template: string,
  parameters: TranslationParameters,
): string =>
  template.replace(/\{(\w+)\}/g, (_, match) =>
    parameters[match] !== undefined ? String(parameters[match]) : `{${match}}`,
  )

export const getStringValue = (
  translations: Translations,
  key: string,
): string | null => {
  const value = getPropertyByPath(translations, key)
  return isString(value) ? value : null
}

export const translate = (
  translations: Translations,
  fallbackTranslations: Translations,
  key: string,
  parameters?: TranslationParameters,
): string => {
  const translationValue =
    getStringValue(translations, key) ??
    getStringValue(fallbackTranslations, key)

  if (!translationValue) return key

  return !isUndefined(parameters)
    ? interpolate(translationValue, parameters)
    : translationValue
}

export type TranslationFunction = (
  key: string,
  parameters?: TranslationParameters,
  language?: string,
) => string

export type OnMissingKeyCallback = (info: {
  key: string
  language: string
}) => void

export const DEFAULT_FALLBACK_LANGUAGE = 'en'

export const createI18n = (
  translations: TranslationsByLanguage,
  language: string,
  fallbackLanguage = DEFAULT_FALLBACK_LANGUAGE,
  onMissingKey?: OnMissingKeyCallback,
): TranslationFunction => {
  let languageTranslations = translations[language]

  if (isUndefined(languageTranslations)) {
    console.warn(
      `No translations found for language: ${language}, falling back to ${fallbackLanguage}`,
    )
    languageTranslations = translations[fallbackLanguage]

    if (isUndefined(languageTranslations)) {
      throw new Error(
        `No translations found for fallback language: ${fallbackLanguage}`,
      )
    }
  }

  return (
    key: string,
    parameters?: TranslationParameters,
    languageOverride?: string,
  ) => {
    const resolvedLanguage = languageOverride ?? language
    const result = translate(
      languageOverride ? translations[languageOverride] : languageTranslations,
      translations[fallbackLanguage],
      key,
      parameters,
    )

    // `translate` returns `key` itself when nothing was found — the only signal available
    // without changing its return shape. Same edge case as any string-equality check: a
    // translation that legitimately equals its key false-positives as "missing".
    if (result === key) onMissingKey?.({ key, language: resolvedLanguage })

    return result
  }
}
